import { CalendarEvent, EventsTypeEnum, ProductionEventType } from '__generated__/types';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { dayjs } from 'common/dayjs';
import routes from 'common/routes';
import { AntBadge, AntBadgeProps } from 'components/ant/AntBadge';
import { AntButton } from 'components/ant/AntButton/AntButton';
import { AntPopconfirm } from 'components/ant/AntPopconfirm';
import { AntPopover } from 'components/ant/AntPopover';
import type { Dayjs } from 'dayjs';
import { LABELS_MAP } from 'pages/CalendarPage/CalendarPage.const';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import s from './CustomCell.module.less';

interface Props {
  event: CalendarEvent;
  onDeleteEvent: (id: number) => void;
  onEditEvent: (event: ProductionEventType) => void;
}

const STATUS_MAP = {
  [EventsTypeEnum.PublicHolidayEvent]: 'success',
  [EventsTypeEnum.ProductionEvent]: 'error',
  [EventsTypeEnum.PoLeadtimeEvent]: 'processing',
};

/** The start of a range repeats only what differs from its end: `28 January – 3 February, 2026`. */
const formatRangeStart = (startDate: Dayjs, endDate: Dayjs) => {
  if (!startDate.isSame(endDate, 'year')) return `${startDate.date()} ${startDate.format('MMMM')}, ${startDate.year()}`;

  if (!startDate.isSame(endDate, 'month')) return `${startDate.date()} ${startDate.format('MMMM')}`;

  return String(startDate.date());
};

export const CustomCell: FC<Props> = ({ event, onDeleteEvent, onEditEvent }) => {
  const startDate = dayjs(event.startDate);
  const endDate = dayjs(event.endDate);
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    //blocking onSelect event bubbling
    <div
      onClick={(e) => {
        e.stopPropagation();
      }}
      role="button"
      tabIndex={0}
    >
      <AntPopover
        trigger="click"
        open={open}
        onOpenChange={(value) => setOpen(value)}
        content={
          <div>
            <div className={s.content}>
              <AntBadge
                status={STATUS_MAP[event.type] as AntBadgeProps['status']}
                text={
                  event.type === EventsTypeEnum.PoLeadtimeEvent && 'providerOrderId' in event ? (
                    <a href={routes.order(event?.providerOrderId)} target="_blank" rel="noopener noreferrer">
                      {event.name}
                    </a>
                  ) : (
                    <b>{event.name}</b>
                  )
                }
              />
              {event.type === EventsTypeEnum.ProductionEvent && 'productionEventId' in event ? (
                <div className={s.buttonsContainer}>
                  <AntButton
                    type="link"
                    icon={<EditOutlined />}
                    onClick={() => {
                      setOpen(false);
                      onEditEvent(event);
                    }}
                    noPadding
                  />
                  <AntPopconfirm
                    title={t('commonPhrases.areYouSure')}
                    onConfirm={() => onDeleteEvent(event.productionEventId)}
                    okText={t('commonPhrases.yes')}
                    cancelText={t('commonPhrases.no')}
                  >
                    <AntButton type="link" noPadding>
                      <DeleteOutlined />
                    </AntButton>
                  </AntPopconfirm>
                </div>
              ) : null}
            </div>

            <div className={s.eventInfo}>
              <div>{LABELS_MAP[event.type](t)}</div>
              <div>
                {startDate.isSame(endDate, 'day')
                  ? `${startDate.date()} ${startDate.format('MMMM')}, ${startDate.year()}`
                  : `${formatRangeStart(startDate, endDate)} – ${endDate.date()} ${endDate.format('MMMM')}, ${endDate.year()}`}
              </div>
            </div>
          </div>
        }
      >
        <AntBadge
          status={STATUS_MAP[event.type] as AntBadgeProps['status']}
          className={s.eventBadge}
          text={event.name}
        />
      </AntPopover>
    </div>
  );
};
